import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BooleanField,
  DataTable,
  DateTimeField,
  Field,
} from '../../../commons/DataTable';
import ActionsField from './ActionsField';
import {
  deleteFile,
  getAllProfileFiles,
  getSignedUrl,
} from '../../../lib/fetch/files';
import { useAlert } from 'react-alert';
import { useLoading } from '../../../commons/Loading';
import { useAuth } from '../../../commons/Auth';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import AddFileButton from './AddFileButton';
import { uploadFileByUser } from '../../../lib/fetch/profiles';
import SwitchField from './SwitchField';
import generatePaginationOptions from '../../../lib/helpers/generatePaginationOptions';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { ContFilterYear, ContPaginationPage } from '../../../styled-components';
import DisponibilityField from './DisponibilityField';
import DocumentTypeField from '../../../commons/DataTable/DocumentTypeField';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const MyDocuments = ({
  uploadFile,
  setUpdatedProfile,
  documentTypes,
  softwareLabels,
}) => {
  const [t] = useTranslation();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [{ newToken, profile = {} } = {}] = useAuth();
  const [allProfileFiles, setAllProfileFiles] = useState([]);
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    active: withDefault(StringParam, localStorage.getItem('active')),
  });

  function onFilter(queryDiff) {
    if (
      (queryDiff.active && queryDiff.active !== active) ||
      (active && queryDiff.active === '') ||
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0;
    }
    setQuery(queryDiff);
  }

  const { limit, offset, active } = query;

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  async function getProfileDocuments() {
    showLoading();

    const { data, error, headers = {} } = await getAllProfileFiles(
      profile._id,
      limit,
      offset,
      newToken,
      active
    );

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    setAllProfileFiles(data.documents);
    setTotal(+headers['x-total-count']);
    hideLoading();
  }

  async function handleDelete({ documentId }) {
    showLoading();

    const { error, data } = await deleteFile({
      documentId,
      newToken,
    });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    setUpdatedProfile(data);
    getProfileDocuments();
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  useEffect(() => {
    getProfileDocuments();
  }, [limit, offset, active, documentTypes]);

  return (
    <>
      <Row className="align-items-center">
        <Col sm="12" md="4" className="select-wrapper">
          <ContFilterYear className="no-width">
            <Row>
              <Col xs="6">
                <Label>Stato documenti</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="active"
                    name="active"
                    value={active}
                    onChange={({ target: { value: active } }) =>
                      onFilter({ active })
                    }
                  >
                    <option value="" defaultChecked>
                      Tutti
                    </option>
                    <option value="ACTIVE">Attivi</option>
                    <option value="NOT_ACTIVE">Scaduti</option>
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>
        <Col xs={8} style={{ marginBottom: 20, textAlign: 'end' }}>
          <AddFileButton
            uploadFile={uploadFile}
            profile={profile}
            documentTypes={documentTypes}
            softwareLabels={softwareLabels}
          />
        </Col>
      </Row>

      <DataTable
        data={allProfileFiles}
        total={total}
        offset={offset}
        limit={limit}
        onFilter={onFilter}
        emptyText={t('COMMON:NO_DATA')}
      >
        <Field title="Nome" source="file.name" className="text-left" />
        <DocumentTypeField
          title="Tipologia"
          source="type.name"
          className="text-left"
        />
        <DateTimeField
          title="Data Caricamento"
          source="file.createdAt"
          format="DD/MM/YYYY"
        />
        <DateTimeField
          title="Data Scadenza"
          source="expireDate"
          format="DD/MM/YYYY"
        />
        <Field title="Caricato da" source="uploadedBy" />
        <BooleanField title="Scaduto" source="expired" />
        <SwitchField
          title="Necessario per contratto"
          source="needed"
          profile={profile}
          getProfileDocuments={getProfileDocuments}
        />
        <DisponibilityField title="Disponibilità" />
        <ActionsField
          title="Azioni"
          handleFileListView={handleFileListView}
          deleteFileConfirm={handleDelete}
        />
      </DataTable>

      <ContPaginationPage>
        <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

        <FormGroup>
          <CustomInput
            type="select"
            className="pagination-select"
            id="perPage"
            name="perPage"
            value={query.limit}
            onChange={({ target: { value: limit } }) => {
              onFilter({ limit });
            }}
          >
            {generatePaginationOptions()}
          </CustomInput>
        </FormGroup>
      </ContPaginationPage>
    </>
  );
};

export default MyDocuments;
